import { SAVE_CONNECTIONS } from './constants';
import { takeLatest, select, put, call } from 'redux-saga/effects';
import { TeamAPI } from '../../utils/API/TeamAPI';
import { GeofenceAPI } from '../../utils/API/GeofenceAPI';
import { selectTeams } from '../../redux/selectors/team.selector';
import { selectGeofences, selectPresence } from '../../redux/selectors/geofence.selector';
import { setDeviceToConnect } from './actions';
import { selectDevices } from '../../redux/selectors/device.selector';
import { selectCurrentUser } from '../../redux/selectors/app.selectors';
import { v4ScopeFor } from '../../api/V4client';
import {
	getV4GeofenceDeviceIds,
	linkV4DeviceToGeofence,
	unlinkV4DeviceFromGeofence,
} from '../V4Geofences/geofences';
import { lookupOrCreateDeviceV4 } from '../../redux/sagas/deviceV4.saga';
import { consoleWarn } from "../../utils/log";

const teamAPI = new TeamAPI();
const geofenceAPI = new GeofenceAPI();

function* resolveV4Scope() {
	const user = yield select(selectCurrentUser());
	if (!user?.accountId) return null;
	try {
		return yield call(() => v4ScopeFor(user.accountId));
	} catch (_) {
		return null;
	}
}

function* resolveDeviceUuid(device) {
	const existing = device?.get?.('uuid') || device?.uuid;
	if (existing) return existing;
	if (device?.isV4Only) return device.id;
	try {
		return yield call(lookupOrCreateDeviceV4, device.id);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(`Failed to resolve V4 UUID for device ${device?.id}:`, e?.message || e);
		return null;
	}
}

function *saveConnections({device, teamIds, geofenceIds}) {
	const devices = yield select(selectDevices());
	device = devices.find(d => +d.id === +device.id);
	if(!device) return consoleWarn(`Cannot save connections: ${JSON.stringify({device})}`);

	const dId = +device.id;
	const teams = yield select(selectTeams());
	for (const i in teams.toArray()) {
		let t = teams.get(i);
		// V4 teams use UUID ids — skip in this V3 dialog.
		if (t?.isV4) continue;
		const hadDevice = t.get('deviceIds').indexOf(dId) >= 0;
		const hasDevice = teamIds.indexOf(+t.id) >= 0;
		if(hadDevice === hasDevice) continue;
		let deviceIds = [...t.get('deviceIds')];
		if(hasDevice) {
			deviceIds.push(dId);
		} else {
			const index = deviceIds.indexOf(dId);
			deviceIds.splice(index, 1);
		}
		yield teamAPI.updateTeam(t.set('deviceIds', deviceIds));
	}

	const geofences = yield select(selectGeofences());
	const presence = yield select(selectPresence());
	const selectedIdSet = new Set((geofenceIds || []).map(String));

	let deviceUuid = null;
	let v4Scope = null;
	const v4GeofencesToTouch = geofences.toArray().filter(g => {
		if (!g?.isV4) return false;
		const gKey = String(g.id);
		const prevIds = getV4GeofenceDeviceIds(g).map(String);
		const had = prevIds.includes(String(device.id))
			|| (device.uuid && prevIds.includes(String(device.uuid)));
		const has = selectedIdSet.has(gKey);
		return had !== has;
	});
	if (v4GeofencesToTouch.length) {
		deviceUuid = yield* resolveDeviceUuid(device);
		v4Scope = yield* resolveV4Scope();
	}

	for (const i in geofences.toArray()) {
		let g = geofences.get(i);
		if (g?.isV4) {
			if (!deviceUuid) continue;
			const gKey = String(g.id);
			const prevIds = getV4GeofenceDeviceIds(g).map(String);
			const had = prevIds.includes(String(device.id))
				|| (device.uuid && prevIds.includes(String(device.uuid)));
			const has = selectedIdSet.has(gKey);
			if (had === has) continue;
			try {
				if (has) {
					yield call(linkV4DeviceToGeofence, g.id, deviceUuid, v4Scope);
				} else {
					yield call(unlinkV4DeviceFromGeofence, g.id, deviceUuid, v4Scope);
				}
			} catch (e) {
				consoleWarn(`V4 geofence ${has ? 'link' : 'unlink'} failed for ${g.id}: ${e?.message || e}`);
			}
			continue;
		}

		const hadDevice = presence.byGeofence(+g.id)[device.id] !== undefined;
		const hasDevice = selectedIdSet.has(String(g.id));
		if(hadDevice === hasDevice) continue;
		let newDevices = devices.filter(d => presence.byGeofence(+g.id)[d.id] !== undefined);
		if(hasDevice) {
			newDevices = newDevices.push(device);
		} else {
			newDevices = newDevices.filter(d => +d.id !== dId);
		}
		newDevices = newDevices.map(d => d.id);
		yield geofenceAPI.updateGeofenceDevices(g, newDevices.toJS());
	}
	yield put(setDeviceToConnect(null));
}

export default function* sendSaga() {
	yield takeLatest(SAVE_CONNECTIONS, saveConnections);
}
